import { useState, useCallback, useRef, useEffect } from 'react';

export interface SearchHit {
  id: string;
  source: string;
  username: string;
  content: string;
  rank: number;
  createdAt: string;
}

export interface SearchResult {
  hits: SearchHit[];
  total: number;
  limit: number;
  offset: number;
  query: string;
}

export type SourceFilter = 'all' | 'twitch' | 'youtube';

export const PAGE_SIZE = 20;

export function useSearch() {
  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Abort in-flight request on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const search = useCallback(async (q: string, source: SourceFilter, offset: number) => {
    const trimmed = q.trim();
    if (!trimmed) {
      setResult(null);
      setError(null);
      return;
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      q: trimmed,
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (source !== 'all') params.set('source', source);

    try {
      const resp = await fetch(`/api/messages/search?${params.toString()}`, {
        credentials: 'include',
        signal: ctrl.signal,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        setError((body as { message?: string }).message ?? `Fehler ${resp.status}`);
        setResult(null);
        return;
      }
      const data = (await resp.json()) as SearchResult;
      setResult(data);
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setError('Suche fehlgeschlagen');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return { result, loading, error, search };
}
